import { DASHBOARD_AUTH_BASE, dashboardHost } from './dashboardAuth';
import { TENANT_CLOSED_TEXT } from './signOutReason';

// The password sign-in's second step (two-step sign-in, "MFA"): the public /v2/dashboard_auth/mfa/ endpoints of
// apiv2/dashboard_auth.openapi.yaml, called with the pending token or the enrollment token of
// POST /v2/edit/login/'s 202. Plain fetch like ./dashboardAuth.js: there is no session yet.
//
// Every call resolves {status, body} (body: the parsed JSON or null) and rejects only on a network error.

// Keyed by the contract's operationIds; mfaContract.test.js holds every method and path to the spec.
export const SIGN_IN_ENDPOINTS = {
    dashboardMfaChallenge: { method: 'POST', path: '/mfa/challenge/' },
    dashboardMfaVerify: { method: 'POST', path: '/mfa/verify/' },
    dashboardMfaEnrollTotp: { method: 'POST', path: '/mfa/enroll/totp/' },
    dashboardMfaEnrollSms: { method: 'POST', path: '/mfa/enroll/sms/' },
    dashboardMfaEnrollTotpConfirm: { method: 'POST', path: '/mfa/enroll/totp/confirm/' },
    dashboardMfaEnrollSmsConfirm: { method: 'POST', path: '/mfa/enroll/sms/confirm/' },
};

async function call(operationId, body, headers = {}) {
    const { method, path } = SIGN_IN_ENDPOINTS[operationId];
    const res = await fetch(`${DASHBOARD_AUTH_BASE}${path}`, {
        method,
        cache: 'no-store',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    return { status: res.status, body: data };
}

// Text the sign-in code (only `sms` is ever sent; the 202 itself sends nothing).
export const sendSignInCode = (pendingToken) =>
    call('dashboardMfaChallenge', { mfa_pending_token: pendingToken, method: 'sms', host: dashboardHost() });

// `method`: totp | sms | recovery_code. 201 = the session (DashboardMfaVerified).
export const verifySignIn = (pendingToken, method, code) =>
    call('dashboardMfaVerify', { mfa_pending_token: pendingToken, method, code, host: dashboardHost() });

const enrollmentHeaders = (enrollmentToken) => ({ 'X-Enrollment-Token': enrollmentToken });

// The setup forced at sign-in. `kind` totp → {device_id, secret, provisioning_uri, qr}; sms (with `phone`) →
// {device_id, sent_to_masked, resend_after} — the same call with the same number is the "Resend".
export const startEnrollment = (enrollmentToken, kind, phone) =>
    (kind === 'sms'
        ? call('dashboardMfaEnrollSms', { phone }, enrollmentHeaders(enrollmentToken))
        : call('dashboardMfaEnrollTotp', {}, enrollmentHeaders(enrollmentToken)));

// 201 = the session and the ten recovery codes (DashboardMfaEnrolled).
export const confirmEnrollment = (enrollmentToken, kind, deviceId, code) =>
    call(kind === 'sms' ? 'dashboardMfaEnrollSmsConfirm' : 'dashboardMfaEnrollTotpConfirm', { device_id: deviceId, code }, enrollmentHeaders(enrollmentToken));

// Words for every refusal code of both contracts (DashboardAuthErrorCode, DashboardMfaErrorCode), used when an
// answer has no `detail` (the backend's own English text, preferred). mfaContract.test.js checks the list.
export const ERROR_TEXT = {
    invalid_request: 'Something was missing. Try again.',
    password_sign_in_disabled: 'Your company signs in with single sign-on.',
    tenant_closed: TENANT_CLOSED_TEXT,
    throttled: 'Too many attempts. Try again later.',
    mfa_pending_expired: 'This sign-in has expired. Start again.',
    method_not_available: 'That method is not set up for your account. Pick another one.',
    invalid_code: 'That code is not right.',
    totp_backoff: 'Too many wrong codes. Wait a few minutes or use a recovery code.',
    sms_cooldown: 'Wait a moment before asking for another code.',
    sms_daily_cap: 'No more text messages can be sent today. Use your authenticator app or a recovery code.',
    sms_enrollment_cap: 'This setup has sent all the text messages it may. Start again, or use an authenticator app.',
    sms_unavailable: 'Text messages are unavailable right now. Use an authenticator app or a recovery code.',
    sms_send_failed: 'The text message could not be sent. Try another method.',
    invalid_phone: 'Enter a mobile phone number.',
    enrollment_expired: 'This setup has expired. Start again.',
    device_not_found: 'This setup step has expired. Start it again.',
    invalid_password: 'That password is not right.',
    cannot_reset_self: 'You cannot reset your own two-step sign-in.',
    permission_required: 'You do not have permission to reset two-step sign-in.',
    session_mfa_required: 'Sign in with two-step sign-in to reset a colleague\'s.',
    mfa_mandatory: 'Your company requires two-step sign-in; it cannot be turned off.',
};

// What to tell the user about a refused answer: the backend's `detail`, else the code's words, else `fallback`;
// plus how many tries are left after a wrong code.
export function describeRefusal(body, fallback = 'Something went wrong. Try again.') {
    const code = body && typeof body.code === 'string' ? body.code : null;
    const text = (body && typeof body.detail === 'string' && body.detail) || (code && ERROR_TEXT[code]) || fallback;
    if (code === 'invalid_code' && Number.isInteger(body.attempts_left)) {
        const n = body.attempts_left;
        return n === 0 ? `${text} No attempts left.` : `${text} ${n} attempt${n === 1 ? '' : 's'} left.`;
    }
    return text;
}
